// src/asset-delivery/resolver.ts

import type {
  PlaybackItem,
  PlaybackTarget,
  ResolvedPlayback,
} from "./orchestrator/types";
import { AssetResolver } from "./resolver/AssetResolver";
import { isPersistedObjectId } from "@/utils/mediaTools/isPersistedObject";

type ResolveOptions = {
  forceRefresh?: boolean;
};

const resolver = new AssetResolver();

// Build stable cache key only for real DB objects
function targetKey(t: PlaybackTarget): string | null {

  // content type target
  if ("content_type_id" in t) {
    if (!isPersistedObjectId(t.object_id)) return null;
    return `ct:${t.content_type_id}:${t.object_id}`;
  }

  // slug target (always safe)
  if ("slug" in t) {
    return `am:${t.app_label}:${t.model}:slug:${t.slug}`;
  }

  // normal app/model/id target
  if (!isPersistedObjectId(t.object_id)) return null;

  return `am:${t.app_label}:${t.model}:id:${t.object_id}`;
}


export async function resolvePlayback(
  item: PlaybackItem,
  opts?: ResolveOptions
): Promise<ResolvedPlayback> {

  // 🚫 prevent backend request for optimistic objects
  const key = targetKey(item.target);

  if (!key) {
    return {
      url: "",
      expiresAt: undefined,
    };
  }

  return resolver.resolve(item, opts);
}


export function resolveVideoPlayback(
  target: PlaybackTarget,
  opts?: ResolveOptions,
  fieldName = "video"
): Promise<ResolvedPlayback> {

  const key = targetKey(target);
  if (!key) return Promise.resolve({ url: "" });

  return resolvePlayback(
    {
      id: `video:${fieldName}:${key}`,
      type: "video",
      target,
      fieldName,
    },
    opts
  );
}


export function resolveAudioPlayback(
  target: PlaybackTarget,
  opts?: ResolveOptions,
  fieldName = "audio"
): Promise<ResolvedPlayback> {

  const key = targetKey(target);
  if (!key) return Promise.resolve({ url: "" });

  return resolvePlayback(
    {
      id: `audio:${fieldName}:${key}`,
      type: "audio",
      target,
      fieldName,
    },
    opts
  );
}


export function resolveImagePlayback(
  target: PlaybackTarget,
  opts?: ResolveOptions,
  fieldName = "image"
): Promise<ResolvedPlayback> {

  const key = targetKey(target);
  if (!key) return Promise.resolve({ url: "" });

  return resolvePlayback(
    {
      id: `image:${fieldName}:${key}`,
      type: "image",
      target,
      fieldName,
    },
    opts
  );
}


export function resolveThumbnailPlayback(
  target: PlaybackTarget,
  opts?: ResolveOptions,
  fieldName = "thumbnail"
): Promise<ResolvedPlayback> {

  const key = targetKey(target);
  if (!key) return Promise.resolve({ url: "" });

  return resolvePlayback(
    {
      id: `thumbnail:${fieldName}:${key}`,
      type: "thumbnail",
      target,
      fieldName,
    },
    opts
  );
}


export function resolveFilePlayback(
  target: PlaybackTarget,
  opts?: ResolveOptions,
  fieldName = "file"
): Promise<ResolvedPlayback> {

  const key = targetKey(target);
  if (!key) return Promise.resolve({ url: "" });

  return resolvePlayback(
    {
      id: `file:${fieldName}:${key}`,
      type: "file",
      target,
      fieldName,
    },
    opts
  );
}
